/**
 * Which planning fases a vordering create marks (ADR-064) — built from the ticked
 * collectors AND the ticked interventie groups. An interventie group carries the
 * fase it was photographed under (`…/interventie/fase-{id}/…`); a group from the
 * old folder layout has `faseId == null` and has no planning task to mark.
 */

export interface FlipFase {
  id: number;
  name: string;
  date: string | null;
  /** Ticked collectors in this fase (0 for a fase reached only through interventie). */
  count: number;
  m2: number;
  /** Ticked interventie groups in this fase. */
  interventieCount: number;
  // Three states for the planning-flip guidance: undated (no planningsdatum —
  // nothing to flip), future (blijft ongewijzigd), past/today (→ gevorderd). An
  // empty date must NOT fall through to the past branch and show a false "markeer now".
  isFuture: boolean;
  undated: boolean;
}

/**
 * Per-fase rollup of the current selection, sorted by planned date (undated
 * first, as before) with the fase id as tie-break, so "the last fase" (the
 * einde-werken target) is deterministic when two fases share a date_deadline.
 */
export function selectedFasesForFlip(
  collectors: Array<{ faseId: number | null; m2: number }>,
  interventieFaseIds: Array<number | null | undefined>,
  fases: Array<{ id: number; name: string; date_deadline: string }>,
  today: string,
): FlipFase[] {
  const byFase = new Map<number, FlipFase>();
  const entry = (fid: number): FlipFase => {
    let cur = byFase.get(fid);
    if (!cur) {
      const fase = fases.find((f) => f.id === fid);
      const date = fase?.date_deadline || null;
      cur = {
        id: fid,
        name: fase?.name ?? `Fase ${fid}`,
        date,
        count: 0,
        m2: 0,
        interventieCount: 0,
        isFuture: !!date && date.slice(0, 10) > today,
        undated: !date,
      };
      byFase.set(fid, cur);
    }
    return cur;
  };
  for (const c of collectors) {
    if (c.faseId === null) continue; // "Geen fase" collectors have no planning task to flip
    const cur = entry(c.faseId);
    cur.count += 1;
    cur.m2 += c.m2 || 0;
  }
  for (const fid of interventieFaseIds) {
    if (fid == null) continue; // old folder layout: no fase to flip
    entry(fid).interventieCount += 1;
  }
  return [...byFase.values()].sort((a, b) => {
    const da = String(a.date || '').slice(0, 10);
    const db = String(b.date || '').slice(0, 10);
    return da.localeCompare(db) || a.id - b.id;
  });
}

/**
 * The today/past fases the create will auto-flip (future + undated fases are
 * left untouched). Date-ascending, so the LAST id is the latest-planned fase —
 * the one that becomes «einde werken» when that toggle is on.
 */
export function eligibleFlipIdsOf(selected: FlipFase[]): number[] {
  return selected.filter((f) => !f.isFuture && !f.undated).map((f) => f.id);
}
